// Global theme model for innernet.
//
// Two independent knobs drive the whole look:
//   • background  — user-customizable (settings), persisted. Exactly the
//                   bg catalogue from the bloom visualizer plus a custom pick.
//   • organism    — NOT user-customizable. Its hue/shape come from the
//                   personality observers (Self Map). It only *reacts* to the
//                   background (glows on dark grounds, watercolor on light).
//
// Everything visible — ink, labels, the logo, hairlines — is tinted by a
// single `--organism-hue`, so the UI subtly carries the organism's tone. Any
// element we add later just reads `--ink` / the tokens and inherits the link.

import {
  BACKGROUNDS,
  chromeState,
  derivePalette,
  hslToRgb,
  rgbToCss,
  type RGB,
  type ColorState,
  type Background,
} from "./organism/color";
import { applyGroundGradient, groundStops, readGradLevel, rgbToHsv01 } from "./ground";

export { BACKGROUNDS };
export type { RGB, ColorState, Background };

// The organism's default character before any personality data exists.
// Wider hue spread so it reads as a living watercolor — green body, real
// sky-blue petals, warm sand fringes (the reference renders) — instead of a
// single green mass. Primary stays green: it drives the global ink hue.
export const DEFAULT_ORGANISM: ColorState = {
  primary: { hue: 108, sat: 0.6, light: 0.52 }, // green
  secondary: { hue: 202, sat: 0.56, light: 0.56 }, // sky blue
  tinge: { hue: 44, sat: 0.58, light: 0.58 }, // warm sand / ochre accent
  contrast: 0.62,
};

// The showpiece — what a visitor meets before any personality exists. Not a
// user's organism: the most expansive, appealing rendition of the species.
// Richer saturation, wider spread, stronger pop than the default.
export const SHOWPIECE_ORGANISM: ColorState = {
  primary: { hue: 104, sat: 0.66, light: 0.5 }, // vivid leaf green
  secondary: { hue: 196, sat: 0.64, light: 0.55 }, // bright sky
  tinge: { hue: 38, sat: 0.68, light: 0.57 }, // amber
  contrast: 0.74,
};

// The default ground — the exact warm cream from the design (#fffaf1).
export const DEFAULT_BG: Background = {
  id: "innernet",
  label: "innernet",
  rgb: [255 / 255, 250 / 255, 241 / 255],
};

// System-dark defaults — reused from the existing BACKGROUNDS catalogue (never
// invented) for visitors whose OS reports `prefers-color-scheme: dark` and who
// have no persisted ground yet. `slate` is the dashboard's dark-ground default
// (a blue-grey, not jet black); `midnight` is a deeper grey-blue reserved for
// the /docs surface, which always defaults dark regardless of the dashboard
// preference.
export const DEFAULT_DARK_BG: Background = findBg("slate");
export const DOCS_DARK_BG: Background = findBg("midnight");

export const STORAGE_KEY = "innernet-theme";

// The ground and the organism share this one key (the boot script reads it in
// one go) but have SEPARATE writers that merge into it, so neither can clobber
// the other with a value it never meant to save. All of it optional: a key
// written by the organism alone, before any ground is known, is valid.
export type StoredTheme = {
  bgId?: string;
  bg?: string; // css color, the boot script's paint
  // the ground itself, 0..1. The css string above rounds to whole channels and
  // used to be the only thing kept, so a ground off the catalogue (a dial pick,
  // a minted one) could never be restored, and every reload fell back to cream.
  rgb?: RGB;
  inkSat?: string;
  inkLight?: string;
  // organism palette mirrored so the no-flash boot script can repaint the whole
  // UI (folders, avatar, glows) on the FIRST paint — never a default-green flash
  // before the engine mounts. Written on every signed-in load.
  oHue?: string;
  oColor?: string;
  oCore?: string;
  oFringe?: string;
  onOrg?: string;
};

// ── THE MINTED GROUND ─────────────────────────────────────────────────────────
// A new person never lands on plain cream. Their ground is tinted from the
// netti they were just born with: its representative hue (chromeState, the hue
// the whole UI reads as this netti's colour), a little saturated, a little
// deeper than the default. It is EXACTLY the birth room's resting wash
// (OnboardingBirth reads these two numbers from here), so the last frame of the
// birth and the first frame of the dashboard are the same pixel. One place to
// tune it. Pale by construction: no hue crosses the ink switch (theme.test).
export const MINTED_GROUND_SAT = 0.66;
export const MINTED_GROUND_LIGHT = 0.85;
/** the id a minted ground is saved under, until the person picks their own. */
export const MINTED_BG_ID = "minted";

export function mintedGround(hue: number): RGB {
  const h = Number.isFinite(hue) ? ((hue % 360) + 360) % 360 : 0;
  return hslToRgb({ h, s: MINTED_GROUND_SAT, l: MINTED_GROUND_LIGHT }) as RGB;
}

/** the ground a palette mints — through its representative hue, never a pin. */
export function mintedGroundOf(palette: ColorState): RGB {
  return mintedGround(chromeState(palette).primary.hue);
}

/** A saved ground as the account row and the dashboard carry it. */
export type GroundRow = { bgId: string; rgb: RGB };

// three channels on 0..1. A hair of float error either side (an HSV pick at full
// brightness lands on 1.0000000000000002) is the same colour, not a broken one;
// anything further out is another scale or garbage.
const isRgb = (v: unknown): v is RGB =>
  Array.isArray(v) && v.length === 3 && v.every((c) => typeof c === "number" && Number.isFinite(c) && c >= -1e-6 && c <= 1 + 1e-6);
const clampRgb = (v: RGB): RGB => v.map((c) => (c < 0 ? 0 : c > 1 ? 1 : c)) as RGB;

/** `user_settings.ground` (or anything shaped like it) → a ground, or null. */
export function parseGroundRow(raw: unknown): GroundRow | null {
  const g = raw as { bgId?: unknown; rgb?: unknown } | null;
  if (!g || typeof g.bgId !== "string" || !g.bgId || !isRgb(g.rgb)) return null;
  return { bgId: g.bgId, rgb: clampRgb(g.rgb) };
}

// ── THE SURFACES THAT WEAR YOUR OWN GROUND ────────────────────────────────────
// One list, read by the boot script (app/layout → lib/groundBoot) and by
// ThemeProvider, which used to keep two hand-synced regexes that had already
// drifted apart (the mirror and the wallpaper painted late). `/ours` is not
// here: a room wears its own ground, and the provider only tracks yours there.
export const OWN_GROUND_PATHS = "dashboard|projects|account|me|connect|connectors|admin|mirror|os-harness|wallpaper";

export function wearsOwnGround(pathname: string, hostname: string, dashboardHost?: string | null): boolean {
  return (
    new RegExp(`^\\/(${OWN_GROUND_PATHS})(\\/|$)`).test(pathname) ||
    // the dashboard host serves the OS home at "/"
    (!!dashboardHost && hostname === dashboardHost && pathname === "/")
  );
}

// Relative luminance (sRGB, perceptual-ish) — decides dark vs light ground.
export function luminance(rgb: RGB): number {
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(rgb[0]) + 0.7152 * lin(rgb[1]) + 0.0722 * lin(rgb[2]);
}

// Readable ink for text sitting ON the organism colour (the avatar initials).
// Chosen from the organism BODY's luminance so it flips correctly whether the
// minted palette is light (→ deep ink) or dark (→ white), independent of the
// page ground. Fixes light-on-light / dark-on-dark invisibility.
export function readableOn(rgb: RGB): string {
  return luminance(rgb) > 0.52 ? "#15171a" : "#ffffff";
}

// Grounds generated ON THE FLY from the user's organism so the background sits
// in unison with their colour — pale, low-chroma washes of the organism's own
// hues. Light by construction (the ink regime stays in its light branch).
export function harmonizedGrounds(organism: ColorState): Background[] {
  const tinge = organism.tinge ?? organism.secondary;
  const mk = (id: string, label: string, h: number, s: number, l: number): Background => ({
    id,
    label,
    rgb: hslToRgb({ h, s, l }) as RGB,
  });
  return [
    mk("o-tint", "tint", organism.primary.hue, 0.16, 0.965),
    mk("o-wash", "wash", tinge.hue, 0.2, 0.955),
    mk("o-veil", "veil", organism.secondary.hue, 0.12, 0.935),
  ];
}

// Ink saturation/lightness regime for a given ground. Light grounds get a
// dark, olive-tinted ink (matches the #36451a of the design); dark grounds
// flip to a soft, low-chroma light ink. Both carry the organism hue.
export function inkRegime(bgRgb: RGB): { inkSat: string; inkLight: string } {
  const dark = luminance(bgRgb) < 0.35;
  return dark ? { inkSat: "24%", inkLight: "90%" } : { inkSat: "42%", inkLight: "19%" };
}

// The full set of CSS custom properties for a (background, organism) pair.
export function computeThemeVars(bgRgb: RGB, organism: ColorState): Record<string, string> {
  // chrome (ink · avatar · accents) is centred on the organism's REPRESENTATIVE
  // hue so it matches the bloom the user actually sees — not the raw primary,
  // which on a split palette can read a wholly different colour.
  const chrome = chromeState(organism);
  const palette = derivePalette(chrome);
  const { inkSat, inkLight } = inkRegime(bgRgb);
  return {
    "--bg": rgbToCss(bgRgb),
    "--organism-hue": String(Math.round(((chrome.primary.hue % 360) + 360) % 360)),
    "--organism-color": rgbToCss(palette.body),
    "--organism-core": rgbToCss(palette.core),
    "--organism-fringe": rgbToCss(palette.fringe),
    "--on-organism": readableOn(palette.body),
    "--ink-sat": inkSat,
    "--ink-light": inkLight,
  };
}

// Apply ONLY the ground vars (--bg + ink regime). Client-only. Kept separate
// from the organism so mounting the provider never clobbers the no-flash /
// identity-set organism colour with a generic default.
export function applyGroundVars(bgRgb: RGB, into?: HTMLElement | null) {
  const { inkSat, inkLight } = inkRegime(bgRgb);
  const root = into ?? document.documentElement;
  if (!root) return;
  root.style.setProperty("--bg", rgbToCss(bgRgb));
  root.style.setProperty("--ink-sat", inkSat);
  root.style.setProperty("--ink-light", inkLight);
  // THE ground gradient rides the ground itself: re-derive its four stops from
  // the new base colour and the stored bender level, or the wash would be left
  // behind in the OLD hue while the ground moved on.
  const { h, s, v } = rgbToHsv01(bgRgb);
  applyGroundGradient(h, s, v, readGradLevel(), root);
}

/**
 * The same ground as a stylesheet rule — what applyGroundVars writes, as text,
 * so a server render can put the person's colour in the HTML itself (a new
 * device, the apex → yours hop) before any script runs. `grad` is the bender
 * level; the server never knows it (it lives on the device), so it renders the
 * solid ground and the device's own bend is painted inline over it.
 */
export function groundRootCss(bgRgb: RGB, grad = 0): string {
  const { inkSat, inkLight } = inkRegime(bgRgb);
  const { h, s, v } = rgbToHsv01(bgRgb);
  const st = groundStops(h, s, v, grad);
  return `:root{--bg:${rgbToCss(bgRgb)};--ink-sat:${inkSat};--ink-light:${inkLight};--g-a:${st.a};--g-b:${st.b};--g-c:${st.c};--g-top:${st.top}}`;
}

/**
 * The browser's own chrome (the phone's status bar, the tab strip) wears the
 * ground too, not the static cream the layout ships. Both theme-color metas
 * (light and dark) take it, since the person picked one colour for both.
 */
export function paintThemeColor(bgRgb: RGB): void {
  if (typeof document === "undefined") return;
  const css = rgbToCss(bgRgb);
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach((m) => {
    if (m.content !== css) m.content = css;
  });
}

// Apply ONLY the organism vars. Called when an identity seeds the organism —
// the single writer for --organism-* on signed-in surfaces.
export function applyOrganismVars(organism: ColorState) {
  // see computeThemeVars — chrome follows the representative hue, in sync with the
  // bloom for every user.
  const chrome = chromeState(organism);
  const palette = derivePalette(chrome);
  const root = document.documentElement;
  root.style.setProperty("--organism-hue", String(Math.round(((chrome.primary.hue % 360) + 360) % 360)));
  root.style.setProperty("--organism-color", rgbToCss(palette.body));
  root.style.setProperty("--organism-core", rgbToCss(palette.core));
  root.style.setProperty("--organism-fringe", rgbToCss(palette.fringe));
  root.style.setProperty("--on-organism", readableOn(palette.body));
}

function readStored(): StoredTheme | null {
  try {
    const t = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null") as StoredTheme | null;
    return t && typeof t === "object" ? t : null;
  } catch {
    return null;
  }
}

function writeStored(patch: StoredTheme): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readStored(), ...patch }));
  } catch {
    /* private mode / quota — vars still applied to :root in-memory */
  }
}

// Persist the GROUND alone, merged into the stored theme. The organism half is
// left exactly as it was: the two are saved by different moments, and the
// organism's writer used to save whatever ground React happened to hold, which
// on a fresh mount was still the cream default.
export function persistGround(bgId: string, bgRgb: RGB): void {
  const { inkSat, inkLight } = inkRegime(bgRgb);
  writeStored({ bgId, bg: rgbToCss(bgRgb), rgb: [bgRgb[0], bgRgb[1], bgRgb[2]], inkSat, inkLight });
}

// Persist the ORGANISM alone (the boot script's --organism-* paint), merged the
// same way, so it never touches a ground it does not know.
export function persistOrganism(organism: ColorState): void {
  const chrome = chromeState(organism);
  const palette = derivePalette(chrome);
  writeStored({
    oHue: String(Math.round(((chrome.primary.hue % 360) + 360) % 360)),
    oColor: rgbToCss(palette.body),
    oCore: rgbToCss(palette.core),
    oFringe: rgbToCss(palette.fringe),
    onOrg: readableOn(palette.body),
  });
}

// Persist the full theme (ground + organism) so the boot script repaints both
// before paint.
export function persistTheme(bgId: string, bgRgb: RGB, organism: ColorState) {
  persistGround(bgId, bgRgb);
  persistOrganism(organism);
}

/**
 * The ground this device saved, or null. Reads the exact `rgb` when it is
 * there; a key from before it was kept falls back to the catalogue by id, then
 * to the css string itself, so a dial ground saved last week still comes back
 * as itself instead of as cream.
 */
export function readStoredGround(): Background | null {
  const t = readStored();
  if (!t?.bgId) return null;
  const cat = BACKGROUNDS.find((b) => b.id === t.bgId) ?? (t.bgId === DEFAULT_BG.id ? DEFAULT_BG : null);
  const rgb = isRgb(t.rgb) ? t.rgb : cat?.rgb ?? cssToRgb(t.bg);
  if (!rgb) return null;
  return { id: t.bgId, label: cat?.label ?? "yours", rgb: clampRgb(rgb) };
}

function cssToRgb(css: string | undefined): RGB | null {
  const m = typeof css === "string" ? css.match(/[\d.]+/g) : null;
  if (!m || m.length < 3) return null;
  const rgb = [Number(m[0]) / 255, Number(m[1]) / 255, Number(m[2]) / 255];
  return isRgb(rgb) ? rgb : null;
}

/** the birth's note to the dashboard (a cookie on the shared domain, so it
 *  crosses the apex → yours hop; OnboardingBirth writes it, OsShell clears it
 *  on adoption, keepNewborn when the arrival keeps the netti itself). */
export const NEWBORN_COOKIE = "innernet_newborn";

/**
 * How long the note's GROUND counts as this person's. The cookie lives a week
 * so the identity can still be adopted, but the ground only has to carry the
 * birth's last frame across one sitting: sign-up right after the birth (an
 * email link lives an hour). Past that the account holds its own ground, and a
 * note nobody cleared would only paint a colour the person may have replaced.
 */
export const NEWBORN_GROUND_MAX_AGE_MS = 60 * 60 * 1000;

/**
 * The ground a just-born netti carries in that note, if any: a new person's
 * colour on a host that has never seen them, before the account knows it.
 * Only while the note is fresh (`at`, when the birth wrote it): an old one, or
 * one from a clock that has since moved by more than the window, is no guide.
 */
export function readNewbornGround(
  cookie: string = typeof document === "undefined" ? "" : document.cookie,
  now: number = Date.now(),
): RGB | null {
  const part = cookie.split("; ").find((c) => c.startsWith(`${NEWBORN_COOKIE}=`));
  if (!part) return null;
  try {
    const note = JSON.parse(decodeURIComponent(part.slice(NEWBORN_COOKIE.length + 1))) as { ground?: unknown; at?: unknown } | null;
    const at = note?.at;
    if (typeof at !== "number" || !(Math.abs(now - at) <= NEWBORN_GROUND_MAX_AGE_MS)) return null;
    const g = note?.ground;
    return isRgb(g) ? clampRgb(g) : null;
  } catch {
    return null;
  }
}

export function findBg(id: string): Background {
  return BACKGROUNDS.find((b) => b.id === id) ?? (id === DEFAULT_BG.id ? DEFAULT_BG : DEFAULT_BG);
}
